import type React from "react";
import { useEffect } from "react";
import { useRouter } from "solito/router";
import { FullscreenSpinner, GrantedTextLogo, usePathname, XStack, YStack } from "@medbillai/ui";
import { getStartedUrl, loginUrl, webSignUpOnboardingUrl, webSignUpOnboardingUsageScopeUrl, webSignUpOnboardingUserNeedsUrl } from "@medbillai/utils/urls";
import { BackgroundRadialGrantedGradient } from "../../components/BackgroundRadialGrantedGradient";
import { HeaderProgressBarLargeOutline, useHeaderProgressBar } from "../../components/progress-bar";
import { analyticsService } from "../../lib/analytics/analytics";
import { useAuth } from "../../lib/auth";
import { useSignUpOnboardingStore } from "../../lib/sign-up-onboarding/signUpOnboardingStore";
const skipSignUpOnboardingUrls = [webSignUpOnboardingUrl(), getStartedUrl(), webSignUpOnboardingUserNeedsUrl(), webSignUpOnboardingUsageScopeUrl()];
const routePercentMap = {
  [webSignUpOnboardingUrl()]: 20,
  [webSignUpOnboardingUserNeedsUrl()]: 40,
  [webSignUpOnboardingUsageScopeUrl()]: 60,
  [loginUrl()]: 80,
  // verifyEmailUrl() requires a param and or the pathname is login
  "/verify-email": 100
};
export type SignUpOnboardingLayoutProps = {
  maxWidth?: number;
  children?: React.ReactNode;
};
export const SignUpOnboardingLayout = ({
  maxWidth = 800,
  children
}: SignUpOnboardingLayoutProps) => {
  const pathname = usePathname();
  const router = useRouter();
  const {
    isSignUpOnboarding
  } = useSignUpOnboardingStore();
  const {
    isAuthenticated,
    isReady
  } = useAuth();
  const {
    animatedProgress
  } = useHeaderProgressBar({
    routePercentMap
  });

  // If the user is authenticated and the pathname is a skip sign up onboarding
  // url, we need to redirect them to the home page so they are not stuck on the
  // sign up onboarding flow.
  useEffect(() => {
    if (isAuthenticated && isReady && skipSignUpOnboardingUrls.includes(pathname)) {
      router.replace("/");
    } else if (pathname === webSignUpOnboardingUrl()) {
      // Track the start of the sign up onboarding flow.
      analyticsService.trackEvent("SignUpOnboardingFlowStarted", {});
    }
  }, [isAuthenticated, isReady, pathname, router]);

  // Show spinner if not ready or if we're about to redirect authenticated users
  if (!isReady || isAuthenticated && isReady && skipSignUpOnboardingUrls.includes(pathname)) {
    return <FullscreenSpinner />;
  }
  return <YStack f={1} overflow="hidden"
  // This is needed so that bottom sheets don't create extra space under the pages
  $platform-web={{
    height: "100vh"
  }} data-sentry-element="YStack" data-sentry-component="SignUpOnboardingLayout" data-sentry-source-file="layout.web.tsx">
      {isSignUpOnboarding ? <HeaderProgressBarLargeOutline animatedProgress={animatedProgress} /> : null}
      <BackgroundRadialGrantedGradient data-sentry-element="BackgroundRadialGrantedGradient" data-sentry-source-file="layout.web.tsx" />
      <YStack gap="$4" px="$4" pt="$4" data-sentry-element="YStack" data-sentry-source-file="layout.web.tsx">
        <XStack jc="center" ai="center" data-sentry-element="XStack" data-sentry-source-file="layout.web.tsx">
          <YStack justifyContent="center" alignItems="center" minHeight={54} data-sentry-element="YStack" data-sentry-source-file="layout.web.tsx">
            <GrantedTextLogo width={80} height={16} data-sentry-element="GrantedTextLogo" data-sentry-source-file="layout.web.tsx" />
          </YStack>
        </XStack>
      </YStack>
      <YStack flex={1} maxWidth={maxWidth} mx="auto" px="$2" width="100%" data-sentry-element="YStack" data-sentry-source-file="layout.web.tsx">
        {children}
      </YStack>
    </YStack>;
};